/**
 * PlatformItemPicker — choose the SWTOR item a treasure reward pays out.
 *
 * Items come from the SWTORBIT platform's catalogue (every item in the game),
 * searched through api-server's `/api/platform/items` proxy. "Market only"
 * (on by default) narrows the search to items with a Cartel Market / Collections
 * listing, which is what treasure rewards almost always are.
 *
 * Shows the current pick with its icon and quality colour. A reward with no
 * platform item is called out: either it is new and nothing is picked yet (it is
 * dropped when saved), or it is UNLINKED — authored against games' retired Cartel
 * catalogue and not converted — and still pays out by name only.
 *
 * Controlled: `onPick` hands back the platform item id and its name.
 */

import { useState } from 'react';
import { platformQualityToCartelItemQuality, type PlatformItemSummary } from '@swtorbit/shared-socket-events';
import { usePlatformItemSearch, usePlatformItemSummaries } from '../../../../hooks/usePlatformItems';
import { ItemIcon } from './ItemIcon';

const RESULTS_LIMIT = 20;

/** `text-quality-*` class for an item's display quality, or '' for none. */
function qualityClass(item: Pick<PlatformItemSummary, 'displayQuality'> | undefined): string {
  const quality = platformQualityToCartelItemQuality(item?.displayQuality?.code);
  return quality ? `text-quality-${quality}` : '';
}

interface PlatformItemPickerProps {
  /** The picked platform item id, if any. */
  itemId?: string;
  /** The name stored with the reward (shown until the item's summary loads, and for unlinked rewards). */
  itemName?: string;
  /** True when the reward still carries a legacy Cartel catalogue id instead of a platform item. */
  legacy?: boolean;
  onPick: (pick: { itemId: string; name: string }) => void;
  disabled?: boolean;
}

export function PlatformItemPicker({ itemId, itemName, legacy = false, onPick, disabled = false }: PlatformItemPickerProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [marketOnly, setMarketOnly] = useState(true);

  const selected = usePlatformItemSummaries([itemId]).get(itemId ?? '');
  const search = usePlatformItemSearch({
    q: open ? query : '',
    ...(marketOnly ? { market: true } : {}),
    limit: RESULTS_LIMIT,
  });

  const pick = (item: PlatformItemSummary) => {
    onPick({ itemId: String(item.id), name: item.name });
    setOpen(false);
    setQuery('');
  };

  const name = selected?.name ?? itemName;

  return (
    <div className="d-flex flex-column gap-2xs">
      <div className="d-flex gap-2xs align-items-center">
        <ItemIcon iconUrl={selected?.icon?.url} name={name ?? 'SWTOR item'} size={28} />
        <span className={`fs-sm flex-1 ${qualityClass(selected)}`}>
          {name ?? <span className="text-slate">No item picked</span>}
        </span>
        <button
          type="button"
          className="btn btn--sm btn--outline btn--slate"
          onClick={() => setOpen((o) => !o)}
          disabled={disabled}
          aria-expanded={open}
        >
          {open ? 'Cancel' : itemId ? 'Change' : 'Pick item'}
        </button>
      </div>

      {!itemId && legacy && (
        <span className="fs-xs text-amber">
          From the old Cartel catalogue, not linked to a SWTOR item: it pays out by name only, with no icon.
          Pick the item to link it.
        </span>
      )}
      {!itemId && !legacy && (
        <span className="fs-xs text-amber">Pick an item. A reward without one is dropped when saved.</span>
      )}

      {open && (
        <div className="d-flex flex-column gap-2xs">
          <div className="d-flex gap-2xs align-items-center">
            <input
              type="search"
              className="input input--sm flex-1"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search SWTOR items"
              aria-label="Search SWTOR items"
              autoFocus
            />
            <label className="fs-xs d-flex align-items-center gap-2xs" style={{ whiteSpace: 'nowrap' }}>
              <input className="checkbox" type="checkbox" checked={marketOnly} onChange={(e) => setMarketOnly(e.target.checked)} />
              Market only
            </label>
          </div>

          {search.loading && <span className="fs-xs text-slate">Searching…</span>}
          {search.error && <span className="fs-xs text-amber">{search.error}</span>}
          {search.page && search.page.items.length === 0 && (
            <span className="fs-xs text-slate">No items match.</span>
          )}
          {search.page && search.page.items.length > 0 && (
            <div className="d-flex flex-column" role="listbox" aria-label="Matching items">
              {search.page.items.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  role="option"
                  aria-selected={String(item.id) === itemId}
                  className="dropdown__item d-flex align-items-center gap-2xs"
                  onClick={() => pick(item)}
                >
                  <ItemIcon iconUrl={item.icon?.url} name={item.name} size={24} />
                  <span className={`fs-sm flex-1 text-start ${qualityClass(item)}`}>{item.name}</span>
                  <span className="fs-xs text-slate">
                    {[item.subcategory?.name ?? item.category?.name, item.rarity?.code !== 'None' ? item.rarity?.label : null]
                      .filter(Boolean)
                      .join(' · ')}
                  </span>
                </button>
              ))}
              {search.page.total > search.page.items.length && (
                <span className="fs-xs text-slate">
                  {search.page.total - search.page.items.length} more. Refine the search to narrow it down.
                </span>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
